import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ExternalLink, Eye } from "lucide-react";
import { useApi } from "../hooks/useApi";
import { formatUtcDateTime } from "../utils/dateTime";
import { fmtMoneyAtLeast } from "../utils/moneyFormat";
import { declaredAnswerProvenance, declaredAnswerText } from "../utils/declaredConsequence";

/**
 * Entry paths for one AI asset (`GET /ai-assets/{id}/entry-paths`): what got in, whose identity it
 * becomes, what that reaches, what it would cost, and what can be done about it.
 *
 * Borrows the attack-path shape (numbered stages) but none of its mechanism: every stage here comes
 * from findings and graph edges the server already holds. The panel raises nothing and requests
 * nothing — it explains existing risks and links to them.
 *
 * Language rule: every string is a statement about what is possible, never about what happened.
 * A leaked secret is "a secret of this type in a repository this asset is associated with", never
 * the asset's own key. An asset with no paths renders nothing: the absence of a known origin is not
 * evidence that there is no way in. The one exception is the owner's declared consequence, which
 * belongs to the asset rather than to a path, so it shows even when no origin is known.
 */

const IDLE = { status: "idle", data: null };

/**
 * Lazy read, fetched while `active` (the Risks & Response tab is open) and refetched when `version`
 * moves, keeping the current content on screen meanwhile. A different asset clears outright.
 * Status: idle | loading | ready | hidden (404, or the sign-in redirect) | error.
 */
export function useEntryPaths(assetId, active, version) {
  const apiCall = useApi();
  const [state, setState] = useState(IDLE);
  const fetchedFor = useRef(-1);

  useEffect(() => {
    setState(IDLE);
    fetchedFor.current = -1;
  }, [assetId]);

  useEffect(() => {
    if (!active) return undefined;
    if (fetchedFor.current === version) return undefined;
    // Cancellation, not just cleanup: an older response must never replace newer state.
    let cancelled = false;
    setState((s) => (s.status === "ready" ? s : { status: "loading", data: null }));
    apiCall("GET", `/ai-assets/${assetId}/entry-paths`)
      .then((d) => {
        if (cancelled) return;
        fetchedFor.current = version;
        // useApi resolves undefined when it redirects to sign-in: show nothing.
        setState(d ? { status: "ready", data: d } : { status: "hidden", data: null });
      })
      .catch((err) => {
        if (cancelled) return;
        fetchedFor.current = version;
        // 404 is the endpoint's answer for "outside your scope" as much as "no such asset".
        setState({ status: err?.response?.status === 404 ? "hidden" : "error", data: null });
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, version, assetId]);

  return state;
}

const ORIGIN_LABEL = {
  ExposedSecret: "Exposed secret",
  StaleProviderCredential: "Stale provider key",
  CredentialAdded: "Credential added",
};

/** Mirrors the server's DataSensitivityLevels.Regulated. */
export const isRegulated = (sensitivity) =>
  /^(phi|pii|financial)$/i.test(String(sensitivity ?? "").trim());

const isThirdParty = (entry) => entry.ownershipDisposition === "ThirdParty";

/** Only http(s) repository links are rendered, so a stored value cannot become a script URL. */
const safeRepoUrl = (url) => (typeof url === "string" && /^https?:\/\//i.test(url.trim()) ? url.trim() : null);

const has = (v) => typeof v === "string" && v.trim() !== "";

// The server's reasons for a null `totalExposure` (AiEntryPathService constants), each mapped to its
// own sentence so "nothing linked", "nothing fair-aligned" and "nothing positive" read differently.
// An unrecognised reason is shown as the server sent it, never replaced by a guess.
const EXPOSURE_REASON_SENTENCE = {
  "no open finding is linked to these paths": "No existing risk is linked to these paths.",
  "none of the linked open findings carries a FAIR expected-loss estimate; amounts from the earlier single-loss method are excluded":
    "No linked risk carries a fair-aligned estimate.",
  "the linked FAIR-aligned findings carry no positive expected-loss estimate":
    "Linked risks carry FAIR estimates, but none is a positive expected loss.",
};

function exposureSentence(reason, hasLinked) {
  if (has(reason)) {
    const mapped = EXPOSURE_REASON_SENTENCE[reason];
    if (mapped) return mapped;
    const text = reason.trim();
    return `${text.charAt(0).toUpperCase()}${text.slice(1)}${/[.!?]$/.test(text) ? "" : "."}`;
  }
  return hasLinked ? "No linked risk carries a fair-aligned estimate." : "No existing risk is linked to these paths.";
}

const STAGE_TITLE = "text-[10px] font-semibold text-content-secondary uppercase tracking-wide";

function Stage({ n, title, children }) {
  return (
    <div className="px-3 py-3 min-w-0">
      <div className="flex gap-2.5">
        <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white bg-slate-500 shrink-0 mt-0.5">
          {n}
        </span>
        <div className="min-w-0 flex-1" data-testid="entry-path-stage">
          <div className={`${STAGE_TITLE} mb-1`} data-testid="entry-path-stage-title">{title}</div>
          <div className="space-y-1.5 text-sm text-content-primary">{children}</div>
        </div>
      </div>
    </div>
  );
}

function OriginFacts({ kind, origin }) {
  const o = origin ?? {};
  if (kind === "ExposedSecret") {
    const href = safeRepoUrl(o.repoWebUrl);
    return (
      <div className="space-y-1 text-xs text-content-secondary">
        {has(o.secretType) && <div><span className="text-content-primary font-medium">{o.secretType}</span></div>}
        {has(o.filePath) && (
          <div className="break-all">
            {o.filePath}{o.lineNumber != null ? ` line ${o.lineNumber}` : ""}
          </div>
        )}
        {has(o.maskedValue) && <div className="font-mono break-all">{o.maskedValue}</div>}
        {href && (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-accent-cyan hover:underline"
          >
            <ExternalLink className="w-3 h-3" />
            Open in repository
          </a>
        )}
      </div>
    );
  }
  if (kind === "StaleProviderCredential") {
    const keys = o.staleKeys ?? [];
    if (keys.length === 0) return null;
    return (
      <ul className="space-y-1 text-xs text-content-secondary">
        {keys.map((k, i) => (
          <li key={`${k.keyName ?? ""}:${k.redactedValue ?? ""}:${i}`} className="break-all">
            <span className="text-content-primary font-medium">{has(k.keyName) ? k.keyName : "Unnamed key"}</span>
            {has(k.redactedValue) && <span className="font-mono"> {k.redactedValue}</span>}
            {has(k.owner) && <span> · owner {k.owner}</span>}
            <span>
              {" · "}
              {k.lastUsedAt ? `last used ${formatUtcDateTime(k.lastUsedAt)}` : "last use not recorded"}
            </span>
          </li>
        ))}
      </ul>
    );
  }
  if (kind === "CredentialAdded") {
    return (
      <div className="space-y-1 text-xs text-content-secondary">
        <div>
          Added by <span className="text-content-primary font-medium">{has(o.actor) ? o.actor : "an unidentified account"}</span>
        </div>
        {o.occurredAt && <div>{formatUtcDateTime(o.occurredAt)}</div>}
      </div>
    );
  }
  return null;
}

/**
 * Whose identity the origin becomes. Only a credential added to the asset's own identity
 * establishes that it authenticates as the asset; an exposed secret stays conditional, and a
 * provider project key is access to the provider project, not to the asset.
 */
function IdentityStage({ kind, asset }) {
  const name = has(asset?.name) ? asset.name : "this asset";
  const identifier = asset?.entraObjectId || asset?.awsResourceArn || null;
  let sentence;
  if (kind === "ExposedSecret") {
    sentence = (
      <>If this secret is a credential for this asset, whoever holds it can act as <strong>{name}</strong>.</>
    );
  } else if (kind === "StaleProviderCredential") {
    sentence = (
      <>
        These keys give access to the provider project or service <strong>{name}</strong> is associated
        with. They do not establish that a key authenticates as this asset.
      </>
    );
  } else if (kind === "CredentialAdded") {
    // The alert never expires and removal is recorded elsewhere, so the credential's current state
    // is not observed: say what it could do, not what it does.
    sentence = (
      <>
        Whoever held this credential could authenticate as <strong>{name}</strong>. Whether it is still
        present is not observed.
      </>
    );
  } else {
    sentence = <>Associated with <strong>{name}</strong>.</>;
  }
  return (
    <>
      <div className="text-xs text-content-secondary">{sentence}</div>
      {identifier && <div className="text-[11px] font-mono text-content-secondary break-all">{identifier}</div>}
    </>
  );
}

function ReachStage({ reach, firstHopOnly }) {
  return (
    <>
      {firstHopOnly && (
        <div className="text-[11px] text-content-secondary italic">
          First hop only: what this asset reaches directly. Axaris does not trace further.
        </div>
      )}
      {reach.length === 0 ? (
        <div className="text-xs text-content-secondary">The graph holds no first-hop reach for this asset.</div>
      ) : (
        <ul className="space-y-1" data-testid="entry-path-reach">
          {reach.map((r, i) => (
            <li key={`${r.nodeKind}:${r.name}:${r.edgeType}:${i}`} className="text-xs" data-testid="entry-path-reach-entry">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-content-primary font-medium break-all">{r.name}</span>
                {isRegulated(r.dataSensitivity) && (
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-50 text-red-700 border border-red-200">
                    Regulated
                  </span>
                )}
                {isThirdParty(r) && (
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-50 text-amber-800 border border-amber-200">
                    Third party
                  </span>
                )}
              </div>
              <div className="text-content-secondary">
                {[r.nodeKind, r.edgeType, r.capabilityClass, has(r.dataSensitivity) ? `${r.dataSensitivity} data` : null,
                  r.confidence ? `${r.confidence} confidence` : null]
                  .filter(Boolean)
                  .join(" · ")}
              </div>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

/** Composed client-side from the fields present: the server's Describe() is not serialized. */
function scopeSentence(scope) {
  const s = scope ?? {};
  if (has(s.label)) {
    const app = has(s.criticality) ? `the ${s.criticality.trim()} application` : "the application";
    const owner = has(s.owner) ? `, owned by ${s.owner.trim()}` : "";
    const provenance = has(s.provenance) ? ` (${s.provenance.trim()})` : "";
    return `Anchored to ${app} '${s.label}'${owner}${provenance}, as your organization declared it. Axaris did not observe this.`;
  }
  if (s.isScoped) return "Scoped to a deal or business unit your organization declared for this asset.";
  return "No deal or business-unit scope could be resolved for this asset, so the organization is the scope.";
}

/** Roadmap item D: what the owner declared would break and who must be told, verbatim, each answer
 *  with its own provenance (where that text was first written). Unanswered questions are omitted;
 *  a null declaration renders nothing. */
const DECLARED_LINES = [
  ["breaksIfStopped", "What breaks if stopped"],
  ["notifyFirst", "Tell first"],
  ["serves", "Serves"],
];

function DeclaredConsequence({ declared }) {
  if (!declared) return null;
  return (
    <div className="mt-1.5 space-y-0.5" data-testid="entry-path-declared-consequence">
      {DECLARED_LINES.filter(([key]) => declaredAnswerText(declared[key]) != null).map(([key, label]) => (
        <div key={key}>
          <div className="text-xs text-content-secondary" data-testid="entry-path-declared-line">
            {label}: <span className="text-content-primary">{declaredAnswerText(declared[key])}</span>
          </div>
          <div className="text-[11px] text-content-secondary" data-testid="entry-path-declared-provenance">
            {declaredAnswerProvenance(declared[key])}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function AiEntryPathPanel({ state, asset = {}, risks = [] }) {
  // Hooks first: rules-of-hooks lint is off in this repo, and a hook below a return ships as React #310.
  const navigate = useNavigate();
  const riskTitles = useMemo(() => {
    const m = new Map();
    for (const r of risks ?? []) m.set(String(r.unifiedRiskId), r.title);
    return m;
  }, [risks]);

  if (state?.status === "error") {
    return <div className="text-xs text-content-secondary">Entry paths could not be loaded</div>;
  }
  if (state?.status !== "ready") return null;
  const data = state.data ?? {};
  const paths = data.paths ?? [];
  const declared = data.declaredConsequence ?? null;
  if (paths.length === 0) {
    // No known origin: the declaration still belongs to the asset, so it alone is shown.
    if (!declared) return null;
    return (
      <div className="border border-border rounded-md bg-theme-primary px-3 py-2.5" data-testid="entry-path-consequence-declared-only">
        <div className={`${STAGE_TITLE} mb-1`}>Consequence</div>
        <DeclaredConsequence declared={declared} />
      </div>
    );
  }

  const firstHopOnly = data.reachIsFirstHopOnly === true;
  const allRiskIds = data.allRiskIds ?? [];
  // Null with a reason, never 0: the server gates the total on fair-align-v1.0 and says why when unset.
  const total = typeof data.totalExposure === "number" ? data.totalExposure : null;
  // Every listed action applies to the asset; only `isAvailable === true` is an option now. The
  // rest are named with the server's reason (fail closed when the flag is missing).
  const listed = data.availableActions ?? [];
  const actions = listed.filter((a) => a?.isAvailable === true);
  const unavailableActions = listed.filter((a) => a?.isAvailable !== true);

  return (
    <div data-testid="entry-path-panel">
      <div className="text-xs font-semibold text-content-secondary uppercase mb-1">Entry paths</div>
      <div className="text-xs text-content-secondary mb-2">
        Ways in that follow from findings Axaris already holds. Each path explains existing risks; none
        is a new finding.
      </div>
      <div className="space-y-2">
        {paths.map((p, i) => (
          <div key={`${p.originKind}:${i}`} className="border border-border rounded-md bg-theme-primary" data-testid="entry-path">
            <div className="px-3 pt-2.5 text-sm font-semibold text-content-primary">
              {ORIGIN_LABEL[p.originKind] || "Origin"}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 divide-y lg:divide-y-0 lg:divide-x divide-border">
              <Stage n={1} title="Origin">
                {has(p.originSummary) && <div className="text-xs text-content-primary">{p.originSummary}</div>}
                <OriginFacts kind={p.originKind} origin={p.origin} />
              </Stage>
              <Stage n={2} title="Identity">
                <IdentityStage kind={p.originKind} asset={asset} />
              </Stage>
              <Stage n={3} title={firstHopOnly ? "Reach (first hop)" : "Reach"}>
                <ReachStage reach={p.reach ?? []} firstHopOnly={firstHopOnly} />
              </Stage>
            </div>
            <div className="px-3 pb-2.5 pt-1 border-t border-border">
              <div className={`${STAGE_TITLE} mt-1.5 mb-1`}>Linked risks</div>
              {(p.implicatedRiskIds ?? []).length === 0 ? (
                <div className="text-xs text-content-secondary">No existing risk is linked to this path.</div>
              ) : (
                <div className="space-y-1">
                  {p.implicatedRiskIds.map((id) => (
                    <div key={id} className="flex items-center gap-2 text-xs" data-testid="entry-path-linked-risk">
                      <span className="flex-1 min-w-0 text-content-primary truncate">{riskTitles.get(String(id)) || id}</span>
                      <button
                        onClick={() => navigate(`/risks?riskId=${id}`)}
                        className="flex items-center gap-1.5 text-xs px-2.5 py-1 border rounded-lg font-medium transition-all bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-600 hover:text-white hover:border-emerald-600 active:scale-95 shrink-0"
                        title="Open in the Risk Register"
                      >
                        <Eye className="w-3 h-3" />
                        View Risk
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Once for the asset: scope belongs to the asset and the total is deduplicated across paths. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 mt-2">
        <div className="border border-border rounded-md bg-theme-primary px-3 py-2.5" data-testid="entry-path-consequence">
          <div className={`${STAGE_TITLE} mb-1`}>Consequence</div>
          <div className="text-xs text-content-secondary">{scopeSentence(data.scope)}</div>
          <DeclaredConsequence declared={declared} />
          <div className="text-xs text-content-secondary mt-1.5">
            {allRiskIds.length > 0 && total !== null && total > 0 ? (
              <>
                FAIR expected annual loss across {allRiskIds.length} linked risk{allRiskIds.length === 1 ? "" : "s"}:{" "}
                <span className="text-sm font-semibold text-content-primary" data-testid="entry-path-exposure">
                  {fmtMoneyAtLeast(total)}
                </span>
              </>
            ) : (
              <span data-testid="entry-path-exposure-unavailable">
                {exposureSentence(data.totalExposureUnavailableReason, allRiskIds.length > 0)}
              </span>
            )}
          </div>
        </div>
        <div className="border border-border rounded-md bg-theme-primary px-3 py-2.5" data-testid="entry-path-containment">
          <div className={`${STAGE_TITLE} mb-1`}>Containment</div>
          {actions.length === 0 ? (
            <>
              <div className="text-xs text-content-secondary">No response action is available for this asset.</div>
              <UnavailableActions actions={unavailableActions} />
            </>
          ) : (
            <>
              <div className="text-xs text-content-secondary mb-1">Options available for this asset:</div>
              <ul className="space-y-1">
                {actions.map((a) => (
                  <li key={`${a.actionType}:${a.targetIdentifier}`} className="text-xs">
                    <span className="text-content-primary">{has(a.description) ? a.description : a.actionType}</span>
                    {has(a.targetIdentifier) && (
                      <span className="font-mono text-content-secondary break-all"> {a.targetIdentifier}</span>
                    )}
                    {a.supportsReversal === false && (
                      <span className="ml-1.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-50 text-red-700 border border-red-200">
                        Permanent
                      </span>
                    )}
                  </li>
                ))}
              </ul>
              <UnavailableActions actions={unavailableActions} />
              <div className="text-xs text-content-secondary mt-1.5">
                If you can manage risks, act on one from the <strong>Response</strong> section below; it
                shows whether an action needs approval.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/** Actions that apply to the asset but cannot be used now: muted, with the reason, no button. */
function UnavailableActions({ actions }) {
  if (!actions.length) return null;
  return (
    <ul className="space-y-1 mt-1" data-testid="entry-path-unavailable-actions">
      {actions.map((a) => (
        <li key={`${a.actionType}:${a.targetIdentifier}`} className="text-xs text-content-secondary opacity-80">
          <span>{has(a.description) ? a.description : a.actionType}</span>
          <span> — {has(a.unavailableReason) ? a.unavailableReason : "This action is not available for this asset right now."}</span>
        </li>
      ))}
    </ul>
  );
}
